<script setup>
import { ref, onMounted, computed } from "vue";
import axios from "axios";
import TurnTestStatusChip from "./TurnTestStatusChip.vue";

const stateText = ref("Getting Turn Servers...");
const stateDetails = ref("");
const stateLoading = ref(true);

const stunServers = ref([]);
const turnServers = ref([]);
const allStunUrls = ref([]);

const emit = defineEmits(["update:modelValue"]);

// --- Settings ---
const showSettings = ref(false);
const settings = ref({
  chunkSizeKB: 16,
  chunksToSend: 8,
  timeoutSeconds: 5,
});

// --- Dialog State ---
const dialogVisible = ref(false);
const selectedServer = ref(null);

// Computed property to sort candidates for the dialog
const sortedCandidatesForDialog = computed(() => {
  if (!selectedServer.value || !selectedServer.value.allCandidates) {
    return [];
  }
  return [...selectedServer.value.allCandidates].sort((a, b) => {
    if (a.wasFilteredOut && !b.wasFilteredOut) return 1;
    if (!a.wasFilteredOut && b.wasFilteredOut) return -1;
    return 0;
  });
});

function showDetails(server) {
  selectedServer.value = server;
  dialogVisible.value = true;
}

const createCandidateKey = (c) => {
  const ip = c.ip || c.address;
  const type = c.candidateType || c.type;
  return `${type}|${c.protocol}|${ip}|${c.port}`;
};

onMounted(() => runAllTests());

async function runAllTests() {
  stateLoading.value = true;
  stateText.value = "Getting Turn Servers...";
  stateDetails.value = "";
  stunServers.value = [];
  turnServers.value = [];
  try {
    await getTurnServers();
    stateLoading.value = false;
    stateText.value = "Checking STUN Servers...";
    await Promise.all(stunServers.value.map((server) => checkStunServer(server)));
    stateText.value = "Checking TURN Servers...";
    await Promise.all(
      turnServers.value
        .filter((server) => server.turnUrls.length > 0)
        .map(async (server) => {
          try {
            await checkTurnServer(server);
          } catch (error) {
            console.error(`Error checking ${server.domain}:`, error);
            stateDetails.value += `\n[${server.domain}] Test failed: ${error.toString().replace("Error: ", "")}`;
            if (server.status !== "ok") server.status = "error";
            if (server.connectionStatus !== "ok") server.connectionStatus = "error";
            if (server.messageStatus !== "ok") server.messageStatus = "error";
            if (server.dataStatus !== "ok") server.dataStatus = "error";
          }
        }),
    );
    stateText.value = "All Checks Completed";
  } catch (error) {
    console.error("Critical error in test setup:", error);
    stateDetails.value += "\n" + error.toString();
    stateText.value = "Error Getting or Processing Servers";
  } finally {
    stateLoading.value = false;
  }
  let emitData = {
    stunServers: stunServers.value,
    turnServers: turnServers.value,
    details: stateDetails.value,
    overallStatus:
      turnServers.value.every((s) => s.status === "ok") ? "ok" : "error",
  };
  emit("update:modelValue", emitData);
}

const raceTimeout = async (promise, timeout, desc = "Timeout") => {
  return Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error(desc)), timeout))]);
};

async function checkStunServer(server) {
  server.status = "checking";
  try {
    await raceTimeout(
      new Promise((resolve, reject) => {
        const pc = new RTCPeerConnection({ iceServers: [{ urls: server.url }] });
        pc.onicecandidate = (e) => {
          if (e.candidate && e.candidate.candidate.includes("typ srflx")) {
            server.status = "ok";
            server.message = e.candidate.candidate;
            pc.close();
            resolve();
          }
        };
        pc.onicegatheringstatechange = () => {
          if (pc.iceGatheringState === "complete") {
            pc.close();
            if (server.status !== "ok") reject(new Error("ICE gathering completed without finding srflx candidate."));
          }
        };
        pc.createDataChannel("stun-test").onopen = () => pc.close();
        pc.createOffer()
          .then((offer) => pc.setLocalDescription(offer))
          .catch(reject);
      }),
      5000,
      "STUN server check timed out.",
    );
  } catch (error) {
    server.status = "error";
    server.message = error.toString().replace("Error: ", "");
    console.error(`STUN Check Failed for ${server.url}:`, error);
  }
}

async function checkTurnServer(server) {
  server.status = "checking";
  server.allCandidates = [];

  const iceConfig = {
    iceServers: [
      { urls: server.turnUrls, username: server.turnUsername, credential: server.turnPassword },
      { urls: server.stunUrls },
    ],
    ...(server.relayOnly ? { iceTransportPolicy: "relay" } : {}),
  };

  const pc1 = new RTCPeerConnection(iceConfig);
  const pc2 = new RTCPeerConnection(iceConfig);
  const pc1CandidateStrings = new Map();
  const pc2CandidateStrings = new Map();

  try {
    const { channel1, channel2 } = await negotiateAndSetupChannels(pc1, pc2, server, pc1CandidateStrings, pc2CandidateStrings);
    const activePair = await raceTimeout(
      testIceConnection(pc1, server, pc1CandidateStrings, pc2CandidateStrings),
      10000,
      "ICE Connection Timeout",
    );

    server.activeLocalCandidate = activePair.local;
    server.activeRemoteCandidate = activePair.remote;

    // --- VALIDATION LOGIC FOR THE CHECKMARK ---
    if (server.activeLocalCandidate || server.activeRemoteCandidate) {
      const activeCandidateString = (server.activeLocalCandidate || "") + (server.activeRemoteCandidate || "");
      if (server.domain.includes("Relay Only")) {
        if (activeCandidateString.includes("typ relay")) server.activeCandidateMatchedFilter = true;
      } else if (server.domain.includes("Full")) {
        server.activeCandidateMatchedFilter = true;
      }
    }

    server.status = "ok";
    await raceTimeout(waitForDataChannelOpen(channel1, channel2), 5000, "Data Channel Open Timeout");
    await raceTimeout(testRtt(channel1, channel2, server), 5000, "RTT Test Timeout");
    await testThroughputAndIntegrity(channel1, channel2, server);
  } finally {
    pc1.close();
    pc2.close();
  }
}

async function negotiateAndSetupChannels(pc1, pc2, server, pc1CandidateStrings, pc2CandidateStrings) {
  pc1.onicecandidate = (e) => {
    if (e.candidate) {
      const wasFilteredOut = !server.candidateFilter(e.candidate.candidate);
      server.allCandidates.push({ raw: e.candidate.candidate, wasFilteredOut: wasFilteredOut });
      pc1CandidateStrings.set(createCandidateKey(e.candidate), e.candidate.candidate);
      if (!wasFilteredOut) pc2.addIceCandidate(e.candidate).catch((err) => console.warn(`Error adding candidate to pc2:`, err));
    }
  };
  pc2.onicecandidate = (e) => {
    if (e.candidate) {
      const wasFilteredOut = !server.candidateFilter(e.candidate.candidate);
      server.allCandidates.push({ raw: e.candidate.candidate, wasFilteredOut: wasFilteredOut });
      pc2CandidateStrings.set(createCandidateKey(e.candidate), e.candidate.candidate);
      if (!wasFilteredOut) pc1.addIceCandidate(e.candidate).catch((err) => console.warn(`Error adding candidate to pc1:`, err));
    }
  };
  const channel1 = pc1.createDataChannel("test-channel");
  const channel2Promise = new Promise((resolve) => {
    pc2.ondatachannel = (event) => resolve(event.channel);
  });
  const offer = await pc1.createOffer();
  await pc1.setLocalDescription(offer);
  await pc2.setRemoteDescription(offer);
  const answer = await pc2.createAnswer();
  await pc2.setLocalDescription(answer);
  await pc1.setRemoteDescription(answer);
  const channel2 = await raceTimeout(channel2Promise, 5000, "Remote peer did not create data channel in time.");
  return { channel1, channel2 };
}

async function lookupCandidateStrings(pc, pc1Candidates, pc2Candidates) {
  try {
    const stats = await pc.getStats();
    let activePair;
    stats.forEach((report) => {
      if (report.type === "candidate-pair" && report.state === "succeeded") activePair = report;
    });
    if (!activePair) return { local: "Could not determine active pair.", remote: "" };
    const localCandidateStats = stats.get(activePair.localCandidateId);
    const remoteCandidateStats = stats.get(activePair.remoteCandidateId);
    if (!localCandidateStats || !remoteCandidateStats) return { local: "Could not find candidate details in stats.", remote: "" };
    const localKey = createCandidateKey(localCandidateStats);
    const remoteKey = createCandidateKey(remoteCandidateStats);
    const localRawString = pc1Candidates.get(localKey) || `[RAW LOCAL NOT FOUND FOR KEY: ${localKey}]`;
    const remoteRawString = pc2Candidates.get(remoteKey) || `[RAW REMOTE NOT FOUND FOR KEY: ${remoteKey}]`;
    return { local: localRawString, remote: remoteRawString };
  } catch (e) {
    console.error("Error looking up candidate strings:", e);
    return { local: "Error retrieving stats for lookup.", remote: "" };
  }
}

async function testIceConnection(pc, server, pc1Candidates, pc2Candidates) {
  server.connectionStatus = "checking";
  return new Promise((resolve, reject) => {
    let gatheringComplete = pc.iceGatheringState === "complete";
    const checkConnection = async () => {
      if (server.connectionStatus === "checking") {
        server.connectionStatus = "ok";
        const pairInfo = await lookupCandidateStrings(pc, pc1Candidates, pc2Candidates);
        resolve(pairInfo);
      }
    };
    if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
      checkConnection();
      return;
    }
    pc.onicegatheringstatechange = () => {
      if (pc.iceGatheringState === "complete") {
        gatheringComplete = true;
        if (pc.iceConnectionState === "failed") reject(new Error("ICE gathering completed, but connection failed."));
      }
    };
    pc.oniceconnectionstatechange = () => {
      switch (pc.iceConnectionState) {
        case "connected":
        case "completed":
          checkConnection();
          break;
        case "failed":
          server.connectionStatus = "error";
          if (gatheringComplete) reject(new Error(`ICE connection failed after gathering completed.`));
          break;
        case "disconnected":
        case "closed":
          server.connectionStatus = "error";
          reject(new Error(`ICE connection entered non-recoverable state: ${pc.iceConnectionState}`));
          break;
      }
    };
  });
}

async function waitForDataChannelOpen(channel1, channel2) {
  return new Promise((resolve, reject) => {
    let openCount = 0;
    const onOpen = () => {
      if (++openCount === 2) resolve();
    };
    if (channel1.readyState === "open") onOpen();
    else channel1.onopen = onOpen;
    if (channel2.readyState === "open") onOpen();
    else channel2.onopen = onOpen;
    channel1.onerror = (e) => reject(new Error(`Channel 1 error: ${e.error.message}`));
    channel2.onerror = (e) => reject(new Error(`Channel 2 error: ${e.error.message}`));
  });
}

async function testRtt(channel1, channel2, server) {
  server.messageStatus = "checking";
  return new Promise((resolve) => {
    channel1.onmessage = (event) => {
      server.rtt = Date.now() - parseInt(event.data, 10);
      server.messageStatus = "ok";
      channel1.onmessage = null;
      resolve();
    };
    channel2.onmessage = (event) => {
      channel2.send(event.data);
      channel2.onmessage = null;
    };
    channel1.send(Date.now().toString());
  });
}

async function testThroughputAndIntegrity(channel1, channel2, server) {
  server.dataStatus = "checking";
  const chunkSize = settings.value.chunkSizeKB * 1024,
    chunksToSend = settings.value.chunksToSend,
    totalSize = chunkSize * chunksToSend;
  const testData = new Uint8Array(chunkSize).fill(Math.floor(Math.random() * 256));
  let receivedChunks = 0,
    receivedSize = 0,
    diffCount = 0,
    startTime = null;
  return new Promise((resolve) => {
    const testTimeout = setTimeout(() => {
      const loss = ((chunksToSend - receivedChunks) / chunksToSend) * 100;
      server.packetLoss = loss;
      if (receivedChunks > 0) {
        const duration = (Date.now() - startTime) / 1000;
        server.throughput = (receivedSize * 8) / duration / 1e6;
        server.dataStatus = diffCount > 0 ? "corrupted" : "error";
      } else {
        server.throughput = 0;
        server.dataStatus = "error";
      }
      resolve();
    }, settings.value.timeoutSeconds * 1000);
    channel2.onmessage = (event) => {
      if (startTime === null) startTime = Date.now();
      receivedChunks++;
      receivedSize += event.data.byteLength;
      if (compareBuffers(testData.buffer, event.data) > 0) diffCount++;
      if (receivedChunks === chunksToSend) {
        clearTimeout(testTimeout);
        const duration = (Date.now() - startTime) / 1000;
        server.throughput = (totalSize * 8) / duration / 1e6;
        server.packetLoss = 0;
        server.dataStatus = diffCount > 0 ? "corrupted" : "ok";
        resolve();
      }
    };
    channel2.onclose = () => clearTimeout(testTimeout);
    for (let i = 0; i < chunksToSend; i++) {
      if (channel1.readyState === "open" && channel1.bufferedAmount < totalSize) channel1.send(testData);
    }
  });
}

async function getTurnServers() {
  const response = await axios.post("/turn", undefined, {
    headers: { RtcId: "purewebrtc-turntest" },
  });
  allStunUrls.value = response.data.stunUrls || [];
  const turnUrlsFromApi = response.data.turnUrls || [];
  stunServers.value = allStunUrls.value.map((url) => ({ url, status: "none", message: "" }));
  const serverMap = {};
  turnUrlsFromApi.forEach((serverUrl) => {
    const match = serverUrl.match(/(?:turn:|turns:)([^?]+)/);
    if (match && match[1]) {
      const id = match[1];
      if (!serverMap[id]) serverMap[id] = [];
      serverMap[id].push(serverUrl);
    }
  });
  let turns = [];
  const baseConf = {
    turnPassword: response.data.turnPassword,
    turnUsername: response.data.turnUsername,
    status: "none",
    connectionStatus: "none",
    messageStatus: "none",
    dataStatus: "none",
    rtt: null,
    throughput: null,
    packetLoss: null,
    allCandidates: [],
    activeLocalCandidate: null,
    activeRemoteCandidate: null,
    activeCandidateMatchedFilter: false, // <-- New property
  };
  const policies = [
    { label: "Full", filter: () => true, relayOnly: false },
    { label: "Relay Only", filter: (c) => c.includes("typ relay"), relayOnly: true },
  ];
  for (const id of Object.keys(serverMap)) {
    const urls = serverMap[id];
    const transports = [
      {
        label: "UDP",
        urls: urls.filter((u) => (u.startsWith("turn:") && !u.includes("transport=")) || u.includes("transport=udp")),
      },
      { label: "TCP", urls: urls.filter((u) => u.startsWith("turn:") && u.includes("transport=tcp")) },
      { label: "TLS (TURNS)", urls: urls.filter((u) => u.startsWith("turns:")) },
    ];
    for (const transport of transports) {
      if (transport.urls.length > 0) {
        for (const policy of policies) {
          turns.push({
            ...baseConf,
            domain: `${id} (${transport.label} - ${policy.label})`,
            stunUrls: allStunUrls.value,
            turnUrls: transport.urls,
            candidateFilter: policy.filter,
            relayOnly: policy.relayOnly,
            allCandidates: [],
          });
        }
      }
    }
  }
  turnServers.value = turns;
}

function compareBuffers(b1, b2) {
  if (b1.byteLength !== b2.byteLength) return 1;
  let d = 0;
  const v1 = new Uint8Array(b1),
    v2 = new Uint8Array(b2);
  for (let i = 0; i < b1.byteLength; i++) {
    if (v1[i] !== v2[i]) d++;
  }
  return d;
}
</script>

<style>
/* Non-scoped style for the dialog content */
.candidate-item {
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 4px;
  background-color: #424242;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  transition: opacity 0.3s ease;
}
.candidate-item.active {
  background-color: #2e7d32;
  color: white;
  font-weight: bold;
}
.candidate-item.active::before {
  content: "✓ ACTIVE: ";
  font-weight: bold;
}
.candidate-item.filtered-out {
  opacity: 0.5;
  background-color: #581e1e;
}
.candidate-item.filtered-out::before {
  content: "✗ FILTERED: ";
  font-weight: normal;
  color: #bdbdbd;
}
</style>

<template>
  <v-card theme="dark" class="mx-auto px-6 py-8" style="max-height: 100vh; max-width: 1600px; overflow: auto">
    <v-card-title class="text-h5 text-center mb-4">
      {{ stateText }}
      <div style="display: inline-flex; gap: 8px; margin-left: 12px; vertical-align: middle">
        <v-btn
          v-if="!stateLoading"
          icon="mdi-refresh"
          variant="text"
          density="compact"
          @click="runAllTests"
          title="Redo Tests"></v-btn>
        <v-btn
          icon="mdi-cog"
          variant="text"
          density="compact"
          @click="showSettings = !showSettings"
          title="Test Settings"></v-btn>
      </div>
    </v-card-title>

    <!-- Settings Panel -->
    <v-expand-transition>
      <v-card-text v-if="showSettings" class="pt-0 pb-2">
        <v-row dense align="center" justify="center" class="mx-auto" style="max-width: 700px">
          <v-col cols="4">
            <v-text-field
              v-model.number="settings.chunkSizeKB"
              label="Chunk Size (KB)"
              type="number"
              density="compact"
              variant="outlined"
              hide-details
              :min="1"
              :max="64"></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model.number="settings.chunksToSend"
              label="Chunk Count"
              type="number"
              density="compact"
              variant="outlined"
              hide-details
              :min="1"
              :max="128"></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model.number="settings.timeoutSeconds"
              label="Timeout (s)"
              type="number"
              density="compact"
              variant="outlined"
              hide-details
              :min="1"
              :max="60"></v-text-field>
          </v-col>
          <v-col cols="12" class="text-center text-caption text-medium-emphasis">
            Total transfer size: {{ settings.chunkSizeKB * settings.chunksToSend }} KB
          </v-col>
        </v-row>
      </v-card-text>
    </v-expand-transition>
    <v-card-text>
      <v-row>
        <!-- Spinner -->
        <v-col style="display: flex; justify-content: center" cols="12" v-if="stateLoading">
          <v-progress-circular indeterminate size="48"></v-progress-circular>
        </v-col>

        <!-- STUN Table -->
        <v-col cols="12" v-if="!stateLoading">
          <h4 class="text-h6 text-center mb-2">STUN Server Tests <span class="text-caption text-medium-emphasis">(informational — does not affect overall status)</span></h4>
          <v-table density="compact">
            <thead>
              <tr>
                <th class="text-left">Server URL</th>
                <th class="text-center" style="width: 100px">Status</th>
                <th class="text-left">Details / Discovered Candidate</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="server in stunServers" :key="server.url">
                <td>{{ server.url }}</td>
                <td><turn-test-status-chip :state="server.status" /></td>
                <td class="text-caption">
                  <div style="max-width: 450px; overflow-x: auto; white-space: nowrap; padding: 2px 0">
                    {{ server.message }}
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>

        <!-- TURN Table -->
        <v-col cols="12" v-if="!stateLoading && turnServers.length > 0">
          <h4 class="text-h6 text-center mt-6 mb-2">TURN Server Tests</h4>
          <v-table density="compact">
            <thead>
              <tr>
                <th class="text-left">Server Configuration</th>
                <th class="text-center">Overall</th>
                <th class="text-center">ICE Connect</th>
                <th class="text-center">RTT (ms)</th>
                <th class="text-center">Msg Integrity</th>
                <th class="text-center">Throughput (Mbps)</th>
                <th class="text-center">Packet Loss (%)</th>
                <th class="text-center">Data Integrity</th>
                <th class="text-center">Candidates</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="server in turnServers" :key="server.domain">
                <td>{{ server.domain }}</td>
                <td><turn-test-status-chip :state="server.status" /></td>
                <td><turn-test-status-chip :state="server.connectionStatus" /></td>
                <td class="text-center font-weight-bold">{{ server.rtt !== null ? server.rtt : "N/A" }}</td>
                <td><turn-test-status-chip :state="server.messageStatus" /></td>
                <td class="text-center font-weight-bold">
                  {{ server.throughput !== null ? server.throughput.toFixed(2) : "N/A" }}
                </td>
                <td class="text-center font-weight-bold">
                  {{ server.packetLoss !== null ? server.packetLoss.toFixed(1) : "N/A" }}
                </td>
                <td><turn-test-status-chip :state="server.dataStatus" /></td>
                <td class="text-center">
                  <div style="display: flex; align-items: center; justify-content: center; gap: 4px">
                    <v-btn
                      v-if="server.allCandidates && server.allCandidates.length > 0"
                      icon="mdi-format-list-bulleted-square"
                      variant="text"
                      density="compact"
                      @click="showDetails(server)"></v-btn>
                    <!-- This icon will only appear if the validation check passes -->
                    <v-icon
                      v-if="server.activeCandidateMatchedFilter"
                      color="success"
                      size="small"
                      icon="mdi-check-circle"></v-icon>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>

        <!-- Log Details -->
        <v-col cols="12" v-if="stateDetails && !stateLoading">
          <h5 class="text-center mt-6">Log Details</h5>
          <pre
            style="
              white-space: pre-wrap;
              background-color: #333;
              color: #eee;
              border: 1px solid #555;
              padding: 10px;
              border-radius: 4px;
              font-family: monospace;
            "
            >{{ stateDetails }}</pre
          >
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Candidate Details Dialog -->
    <v-dialog v-model="dialogVisible" max-width="900">
      <v-card v-if="selectedServer" theme="dark">
        <v-card-title class="text-h6"> Candidate Details for: {{ selectedServer.domain }} </v-card-title>
        <v-card-text>
          <div v-if="!sortedCandidatesForDialog.length">No candidates were generated for this test.</div>
          <div v-else>
            <p class="text-caption mb-2">
              All ICE candidates gathered. Active pair is green. Filtered-out candidates (not exchanged) are dimmed.
            </p>
            <div
              v-for="(candidateObj, index) in sortedCandidatesForDialog"
              :key="index"
              class="candidate-item"
              :class="{
                active:
                  candidateObj.raw === selectedServer.activeLocalCandidate ||
                  candidateObj.raw === selectedServer.activeRemoteCandidate,
                'filtered-out': candidateObj.wasFilteredOut,
              }">
              {{ candidateObj.raw }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="dialogVisible = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
